import React, { useState, useEffect } from 'react';
import './BoardPage.css';
import { Language, t } from '../../utils/i18n';
import {
  getSparksFromCache,
  saveSparksToCache,
  updateSparkInCache,
  deleteSparkFromCache
} from '../../utils/sparkCache';

type SparkStatus = 'captured' | 'thinking' | 'experimenting' | 'archived';

interface Spark {
  id: string;
  title: string;
  description?: string;
  status: SparkStatus;
  project_tag: string;
  creator_comment?: string;
  created_at: string;
  updated_at: string;
}

interface SparkColumn {
  status: SparkStatus;
  title: string;
  color: string;
  icon: string;
}

interface BoardPageProps {
  onBackToHome: () => void;
  onNavigateToCapture: () => void;
  lang: Language;
}

const getCOLUMNS = (lang: Language): SparkColumn[] => [
  { status: 'captured', title: t('captured', lang), color: 'blue', icon: '💡' },
  { status: 'thinking', title: t('thinking', lang), color: 'yellow', icon: '🤔' },
  { status: 'experimenting', title: t('experimenting', lang), color: 'green', icon: '🔬' }
];

const PROJECT_TAG_LABELS: Record<string, string> = {
  douya: 'Douya',
  opensight: 'OpenSight',
  deepseek: 'DeepSeek',
  experiment: 'Experiment',
  other: 'Other'
};

export default function BoardPage({ onBackToHome, onNavigateToCapture, lang }: BoardPageProps) {
  const [sparks, setSparks] = useState<Spark[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<SparkStatus | 'all'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editingSpark, setEditingSpark] = useState<Spark | null>(null);

  // 添加 Esc 键退出编辑
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && editingSpark) {
        setEditingSpark(null);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [editingSpark]);

  useEffect(() => {
    loadSparks();
  }, []);

  const loadSparks = async () => {
    try {
      setLoading(true);

      // 1. 先从本地缓存加载（秒开）
      const cachedSparks = getSparksFromCache();
      if (cachedSparks && cachedSparks.length > 0) {
        setSparks(cachedSparks);
        setLoading(false); // 立即显示，不等API
      }

      // 2. 后台请求 API 更新数据
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/sparks', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);


      const data = await response.json();

      if (data.success && data.sparks) {
        setSparks(data.sparks);
        saveSparksToCache(data.sparks); // 更新缓存
      } else {
        console.error('❌ [Board] API returned error:', data.error);
        // 如果API失败，保留缓存数据，不弹窗打扰用户
        if (!cachedSparks) {
          alert(`加载失败: ${data.error || '未知错误'}`);
        }
      }
    } catch (error) {
      console.error('❌ [Board] Load sparks failed:', error);
      if (error.name === 'AbortError') {
        // 超时也不弹窗，如果缓存可用就继续使用
        const cached = getSparksFromCache();
        if (!cached) {
          alert('请求超时：API服务无响应（请确认Wrangler是否运行）');
        }
      } else {
        const cached = getSparksFromCache();
        if (!cached) {
          alert(`网络错误: ${error.message}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (sparkId: string, newStatus: SparkStatus, note?: string) => {
    try {
      setUpdatingId(sparkId);

      const response = await fetch(`/api/sparks/${sparkId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          note: note || `Updated to ${newStatus}`
        })
      });

      const data = await response.json();

      if (data.success) {
        setSparks(prev =>
          prev.map(spark =>
            spark.id === sparkId ? { ...spark, ...data.spark } : spark
          )
        );
        // 同步更新缓存
        updateSparkInCache(data.spark);
      } else {
        alert(`Update failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Update status failed:', error);
      alert('Network error');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteSpark = async (sparkId: string) => {
    if (!confirm(t('deleteConfirm', lang))) return;

    try {
      setUpdatingId(sparkId);

      const response = await fetch(`/api/sparks/${sparkId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSparks(prev => prev.filter(s => s.id !== sparkId));
        // 同步更新缓存
        deleteSparkFromCache(sparkId);
      } else {
        alert(`Delete failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Network error');
    } finally {
      setUpdatingId(null);
    }
  };

  const getSparksByStatus = (status: SparkStatus) => {
    return sparks.filter(s => s.status === status);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow', lang);
    if (diffMins < 60) return `${diffMins}${t('minsAgo', lang)}`;
    if (diffHours < 24) return `${diffHours}${t('hoursAgo', lang)}`;
    if (diffDays < 7) return `${diffDays}${t('daysAgo', lang)}`;
    return date.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US');
  };

  const startEdit = (spark: Spark) => {
    setEditingSpark(spark);
  };

  const cancelEdit = () => {
    setEditingSpark(null);
  };

  const saveEdit = async (updatedData: Partial<Spark>) => {
    if (!editingSpark) return;

    try {
      setUpdatingId(editingSpark.id);

      const response = await fetch(`/api/sparks/${editingSpark.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedData)
      });

      const data = await response.json();

      if (data.success) {
        setSparks(prev =>
          prev.map(s => s.id === editingSpark.id ? { ...s, ...data.spark } : s)
        );
        // 同步更新缓存
        updateSparkInCache(data.spark);
        setEditingSpark(null);
      } else {
        alert(`Update failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Network error');
    } finally {
      setUpdatingId(null);
    }
  };

  // 判断哪一列应该收缩（当编辑时）
  const getCollapsedColumn = (): SparkStatus | null => {
    if (!editingSpark) return null;

    const columns: SparkStatus[] = ['captured', 'thinking', 'experimenting'];
    const currentIndex = columns.indexOf(editingSpark.status);

    // 始终收缩中间列（thinking），除非在编辑中间列
    if (editingSpark.status === 'thinking') {
      return columns[0]; // 编辑中间列时，收缩左边列
    }

    // 编辑其他列时，收缩中间列
    return columns[1]; // 'thinking'
  };

  // 动态计算网格布局
  const getGridTemplate = () => {
    if (!editingSpark) return undefined;

    const columns: SparkStatus[] = ['captured', 'thinking', 'experimenting'];

    if (editingSpark.status === 'captured') {
      // 编辑左边列：1.5fr 0.5fr 0.5fr
      return { gridTemplateColumns: '1.5fr 0.5fr 0.5fr' };
    }

    if (editingSpark.status === 'thinking') {
      // 编辑中间列：0.5fr 1.5fr 0.5fr
      return { gridTemplateColumns: '0.5fr 1.5fr 0.5fr' };
    }

    if (editingSpark.status === 'experimenting') {
      // 编辑右边列：0.5fr 0.5fr 1.5fr
      return { gridTemplateColumns: '0.5fr 0.5fr 1.5fr' };
    }

    return undefined;
  };

  if (loading) {
    return (
      <div className="spark-board-loading">
        <div className="spinner"></div>
        <p>{t('loading', lang)}</p>
      </div>
    );
  }

  return (
    <div className="spark-board-container">
      <div className="spark-board-header">
        <div>
          <h1>{t('sparkBoard', lang)}</h1>
          <p className="subtitle">{t('manageIdeas', lang)}</p>
        </div>
        <div className="header-actions">
          <button onClick={onBackToHome} className="btn btn-secondary">
            ← {t('backToHome', lang)}
          </button>
          <button onClick={loadSparks} className="btn btn-refresh" disabled={loading}>
            {t('refresh', lang)}
          </button>
          <button onClick={onNavigateToCapture} className="btn btn-primary">
            {t('newSpark', lang)}
          </button>
        </div>
      </div>

      <div className="spark-stats">
        <div className="stat-item">
          <span className="stat-label">{t('total', lang)}</span>
          <span className="stat-value">{sparks.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('captured', lang)}</span>
          <span className="stat-value stat-blue">{getSparksByStatus('captured').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('thinking', lang)}</span>
          <span className="stat-value stat-yellow">{getSparksByStatus('thinking').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('experimenting', lang)}</span>
          <span className="stat-value stat-green">{getSparksByStatus('experimenting').length}</span>
        </div>
      </div>

      <div
        className={`spark-board-columns ${editingSpark ? 'editing-mode' : ''}`}
        style={editingSpark ? getGridTemplate() : undefined}
      >
        {getCOLUMNS(lang).map(column => {
          const columnSparks = getSparksByStatus(column.status);
          const isCollapsed = editingSpark && getCollapsedColumn() === column.status;
          const isEditingColumn = editingSpark && editingSpark.status === column.status;

          return (
            <div
              key={column.status}
              className={`spark-column column-${column.color} ${
                isCollapsed ? 'collapsed' : ''
              } ${isEditingColumn ? 'expanded' : ''}`}
            >
              <div className="column-header">
                <span className="column-icon">{column.icon}</span>
                <h2>{column.title}</h2>
                <span className="column-count">{columnSparks.length}</span>
              </div>

              {isEditingColumn && editingSpark && (
                <div className="edit-panel">
                  <EditSparkForm
                    spark={editingSpark}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    lang={lang}
                  />
                </div>
              )}

              {!isEditingColumn && (
                <div className="column-sparks">
                  {columnSparks.length === 0 ? (
                    <div className="empty-state">
                      <p>{t('noSparks', lang)}</p>
                    </div>
                  ) : (
                    columnSparks.map(spark => (
                      <div
                        key={spark.id}
                        className={`spark-card ${updatingId === spark.id ? 'updating' : ''}`}
                        onClick={() => startEdit(spark)}
                      >
                      <div className="spark-tags">
                        <span className="tag tag-project">
                          {PROJECT_TAG_LABELS[spark.project_tag] || spark.project_tag}
                        </span>
                      </div>

                      <h3 className="spark-title">{spark.title}</h3>

                      {spark.description && (
                        <p className="spark-description">{spark.description}</p>
                      )}

                      {spark.creator_comment && (
                        <div className="spark-comment">
                          💬 {spark.creator_comment}
                        </div>
                      )}

                      <div className="spark-time">
                        {formatDate(spark.updated_at)}
                      </div>

                      <div className="spark-actions"
                          onClick={(e) => e.stopPropagation()}>
                        {column.status === 'captured' && (
                          <>
                            <button
                              onClick={() => changeStatus(spark.id, 'thinking', lang === 'zh' ? '开始思考' : 'Start thinking')}
                              className="btn-action btn-think"
                              disabled={updatingId !== null}
                            >
                              🤔 {t('think', lang)}
                            </button>
                            <button
                              onClick={() => changeStatus(spark.id, 'experimenting', lang === 'zh' ? '开始实验' : 'Start experimenting')}
                              className="btn-action btn-experiment"
                              disabled={updatingId !== null}
                            >
                              🔬 {t('experiment', lang)}
                            </button>
                          </>
                        )}

                        {column.status === 'thinking' && (
                          <>
                            <button
                              onClick={() => changeStatus(spark.id, 'captured', lang === 'zh' ? '回到已捕获' : 'Back to captured')}
                              className="btn-action btn-captured"
                              disabled={updatingId !== null}
                            >
                              ↩️ {t('back', lang)}
                            </button>
                            <button
                              onClick={() => changeStatus(spark.id, 'experimenting', lang === 'zh' ? '开始实验' : 'Start experimenting')}
                              className="btn-action btn-experiment"
                              disabled={updatingId !== null}
                            >
                              🔬 {t('experiment', lang)}
                            </button>
                          </>
                        )}

                        {column.status === 'experimenting' && (
                          <>
                            <button
                              onClick={() => changeStatus(spark.id, 'captured', lang === 'zh' ? '回到已捕获' : 'Back to captured')}
                              className="btn-action btn-captured"
                              disabled={updatingId !== null}
                            >
                              ↩️ {t('back', lang)}
                            </button>
                            <button
                              onClick={() => changeStatus(spark.id, 'archived', lang === 'zh' ? '完成并归档' : 'Complete and archive')}
                              className="btn-action btn-archive"
                              disabled={updatingId !== null}
                            >
                              ✅ {t('done', lang)}
                            </button>
                          </>
                        )}

                        <button
                          onClick={() => deleteSpark(spark.id)}
                          className="btn-action btn-delete"
                          disabled={updatingId !== null}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========================================
//   编辑火花表单组件
// ========================================

interface EditSparkFormProps {
  spark: Spark;
  onSave: (data: Partial<Spark>) => void;
  onCancel: () => void;
  lang: Language;
}

function EditSparkForm({ spark, onSave, onCancel, lang }: EditSparkFormProps) {
  const [title, setTitle] = useState(spark.title);
  const [description, setDescription] = useState(spark.description || '');
  const [comment, setComment] = useState(spark.creator_comment || '');
  const [projectTag, setProjectTag] = useState(spark.project_tag);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    await onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      creator_comment: comment.trim() || undefined,
      project_tag: projectTag
    });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form">
      <h3>{lang === 'zh' ? '编辑火花' : 'Edit Spark'}</h3>

      <div className="form-group">
        <label>
          {t('title', lang)} <span className="required">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="form-input"
          maxLength={200}
          disabled={saving}
          required
        />
        <span className="char-count">{title.length}/200</span>
      </div>

      <div className="form-group">
        <label>{t('project', lang)}</label>
        <select
          value={projectTag}
          onChange={(e) => setProjectTag(e.target.value)}
          className="form-select"
          disabled={saving}
        >
          <option value="douya">Douya</option>
          <option value="opensight">OpenSight</option>
          <option value="deepseek">DeepSeek</option>
          <option value="experiment">Experiment</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="form-group">
        <label>{t('description', lang)}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="form-textarea"
          rows={4}
          maxLength={1000}
          disabled={saving}
        />
        <span className="char-count">{description.length}/1000</span>
      </div>

      <div className="form-group">
        <label>{t('notes', lang)}</label>
        <input
          type="text"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="form-input"
          maxLength={200}
          disabled={saving}
        />
      </div>

      <div className="edit-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={saving}
        >
          {lang === 'zh' ? '取消' : 'Cancel'}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving || !title.trim()}
        >
          {saving ? (lang === 'zh' ? '保存中...' : 'Saving...') : (lang === 'zh' ? '保存' : 'Save')}
        </button>
      </div>
    </form>
  );
}
